import { useState } from 'react';
import { Link } from 'react-router-dom';
import {ChevronDown, ChevronUp, ArrowRight} from 'lucide-react';

interface ArticleMetadata {
  category: string;
  categorySlug: string;
  title: string;
  author: string;
  publishedDate: string;
  readingTime: string;
  featuredImage: {
    url: string;
    alt: string;
  };
}

interface FAQItem {
  question: string;
  answer: string;
}

interface RelatedArticle {
  title: string;
  excerpt: string;
  thumbnail: string;
  slug: string;
  category: string;
}

interface BlogArticleTemplateProps {
  metadata: ArticleMetadata;
  children: React.ReactNode;
  faqItems?: FAQItem[];
  relatedArticles?: RelatedArticle[];
}

export function BlogArticleTemplate({
  metadata,
  children,
  faqItems = [],
  relatedArticles = []
}: BlogArticleTemplateProps) {
  return (
    <div className="min-h-screen bg-white">
      {/* Global Header */}
      <SiteHeader />

      {/* Breadcrumb Navigation */}
      <Breadcrumb 
        category={metadata.category}
        categorySlug={metadata.categorySlug}
        articleTitle={metadata.title}
      />

      {/* Main Article */}
      <article className="pb-20">
        {/* Hero Section */}
        <ArticleHero metadata={metadata} />

        {/* Main Content Wrapper */}
        <div className="max-w-[820px] mx-auto px-6 py-12">
          {/* Article H1 */}
          <header className="mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight">
              {metadata.title}
            </h1>
          </header>

          {/* Article Content */}
          <div className="article-content prose prose-lg max-w-none">
            {children}
          </div>

          {/* CTA Box */}
          <CTABox />

          {/* FAQ Section */}
          {faqItems.length > 0 && <FAQSection items={faqItems} />}

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <RelatedArticlesSection articles={relatedArticles} />
          )}
        </div>
      </article>

      {/* Footer */}
      <SiteFooter />
    </div>
  );
}

function SiteHeader() {
  return (
    <header className="bg-white border-b border-gray-200">
      <nav className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="text-2xl font-bold text-gray-900">
          Mental Control
        </Link>
        <div className="flex items-center gap-8">
          <Link to="/mental-control-overthinking" className="text-gray-600 hover:text-gray-900 transition-colors">
            Articles
          </Link>
          <Link to="/" className="text-gray-600 hover:text-gray-900 transition-colors">
            Ebook
          </Link>
        </div>
      </nav>
    </header>
  );
}

function Breadcrumb({ category, categorySlug, articleTitle }: { 
  category: string; 
  categorySlug: string; 
  articleTitle: string;
}) {
  return (
    <nav className="max-w-[820px] mx-auto px-6 py-4" aria-label="Breadcrumb">
      <ol className="flex items-center gap-2 text-sm text-gray-600">
        <li>
          <Link to="/" className="hover:text-gray-900 transition-colors">
            Home
          </Link>
        </li>
        <li className="text-gray-400">/</li>
        <li>
          <Link to={`/${categorySlug}`} className="hover:text-gray-900 transition-colors">
            {category}
          </Link>
        </li>
        <li className="text-gray-400">/</li>
        <li className="text-gray-900 font-medium truncate max-w-[300px]">
          {articleTitle}
        </li>
      </ol>
    </nav>
  );
}

function ArticleHero({ metadata }: { metadata: ArticleMetadata }) {
  return (
    <section className="relative">
      {/* Featured Image */}
      <div className="relative w-full h-[400px] md:h-[500px] bg-gray-100 overflow-hidden">
        <img
          src={metadata.featuredImage.url}
          alt={metadata.featuredImage.alt}
          loading="lazy"
          className="w-full h-full object-cover"
        />
        {/* Subtle overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
      </div>

      {/* Article Metadata */}
      <div className="max-w-[820px] mx-auto px-6 mt-8">
        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
          <Link 
            to={`/${metadata.categorySlug}`}
            className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-colors font-medium"
          >
            {metadata.category}
          </Link>
          <span className="text-gray-400">•</span>
          <span>By {metadata.author}</span>
          <span className="text-gray-400">•</span>
          <time dateTime={metadata.publishedDate}>{metadata.publishedDate}</time>
          <span className="text-gray-400">•</span>
          <span>{metadata.readingTime} read</span>
        </div>
      </div>
    </section>
  );
}

function CTABox() {
  return (
    <section className="my-16 p-8 md:p-10 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl border border-gray-200">
      <div className="max-w-2xl mx-auto text-center">
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
          Structured Mental Control
        </h2>
        <p className="text-lg text-gray-700 mb-6 leading-relaxed">
          Overthinking does not disappear through calmness.<br />
          It becomes manageable through structure.
        </p>
        <p className="text-base text-gray-600 mb-8">
          👉 Discover the exclusive 3×3×3 Method to stop overthinking.
        </p>
        <Link
          to="/"
          className="inline-block px-8 py-4 bg-[#2C5F7D] text-white font-medium rounded-lg hover:bg-[#234a5f] transition-colors shadow-md"
        >
          Learn the 3×3×3 Mental Control System
        </Link>
        <p className="text-sm text-gray-500 mt-4 italic">
          Designed for analytical minds.
        </p>
      </div>
    </section>
  );
}

function FAQSection({ items }: { items: FAQItem[] }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="my-16" itemScope itemType="https://schema.org/FAQPage">
      <h2 className="text-3xl font-bold text-gray-900 mb-8">
        Frequently Asked Questions
      </h2>
      <div className="space-y-4">
        {items.map((item, index) => (
          <div
            key={index}
            className="border border-gray-200 rounded-lg overflow-hidden"
            itemScope
            itemProp="mainEntity"
            itemType="https://schema.org/Question"
          >
            <button
              onClick={() => toggleFAQ(index)}
              className="w-full flex items-center justify-between p-6 text-left bg-white hover:bg-gray-50 transition-colors"
              aria-expanded={openIndex === index}
            >
              <span className="text-lg font-medium text-gray-900" itemProp="name">
                {item.question}
              </span>
              {openIndex === index ? (
                <ChevronUp className="w-5 h-5 text-gray-600 flex-shrink-0" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-600 flex-shrink-0" />
              )}
            </button>
            {openIndex === index && (
              <div
                className="px-6 pb-6 text-gray-700 leading-relaxed"
                itemScope
                itemProp="acceptedAnswer"
                itemType="https://schema.org/Answer"
              >
                <div itemProp="text">{item.answer}</div>
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

function RelatedArticlesSection({ articles }: { articles: RelatedArticle[] }) {
  return (
    <section className="my-16 pt-16 border-t border-gray-200">
      <h2 className="text-3xl font-bold text-gray-900 mb-8">
        Related Articles
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {articles.map((article, index) => (
          <article key={index} className="group">
            <Link to={`/${article.slug}`} className="block">
              <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden mb-4">
                <img
                  src={article.thumbnail}
                  alt={article.title}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="space-y-2">
                <span className="text-xs text-gray-500 uppercase tracking-wide">
                  {article.category}
                </span>
                <h3 className="text-xl font-bold text-gray-900 group-hover:text-[#2C5F7D] transition-colors leading-tight">
                  {article.title}
                </h3>
                <p className="text-gray-600 text-sm line-clamp-2">
                  {article.excerpt}
                </p>
                <div className="flex items-center gap-2 text-[#2C5F7D] font-medium text-sm pt-2">
                  Read More
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>
          </article>
        ))}
      </div>
    </section>
  );
}

function SiteFooter() {
  return (
    <footer className="bg-gray-50 border-t border-gray-200 py-12">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
          <div className="text-center">
            <h3 className="font-bold text-gray-900 mb-4">Categories</h3>
            <nav className="space-y-2">
              <Link to="/mental-control-overthinking" className="block text-gray-600 hover:text-gray-900 transition-colors">
                Mental Control & Overthinking
              </Link>
              <Link to="/mental-control" className="block text-gray-600 hover:text-gray-900 transition-colors">
                Structured Thinking
              </Link>
            </nav>
          </div>
          <div className="text-center">
            <h3 className="font-bold text-gray-900 mb-4">Resources</h3>
            <nav className="space-y-2">
              <Link to="/" className="block text-gray-600 hover:text-gray-900 transition-colors">
                The Overthinking Reset Ebook
              </Link>
            </nav>
          </div>
          <div className="text-center">
            <h3 className="font-bold text-gray-900 mb-4">Legal</h3>
            <nav className="space-y-2">
              <a href="#" className="block text-gray-600 hover:text-gray-900 transition-colors">
                Privacy Policy
              </a>
              <a href="#" className="block text-gray-600 hover:text-gray-900 transition-colors">
                Terms of Service
              </a>
            </nav>
          </div>
        </div>
        <div className="text-center text-sm text-gray-600 pt-8 border-t border-gray-200">
          <p>© 2024 Mental Control. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
}
